import type { DenyRefillRequestT } from '@lotushealth/lotushealth_js_sdk/schemas';

import {
  DEFAULT_REFILL_DENIAL_REASON,
  REFILL_DENIAL_REASON_OPTIONS,
  useDenyRefillMutation,
} from '@/entities/rx';
import { FormField } from '@/shared/ui/primitives';

import { nullIfBlank } from '../lib';
import { refillDenyDraftSchema, type RefillDenyDraft } from '../model/schemas';
import { useDetailAction } from '../model/useDetailAction';
import { DetailActionForm } from './DetailActionForm';

export interface RefillDenyFormProps {
  readonly refillId: number;
  /** Called after a successful deny (e.g. to close the parent sheet). */
  readonly onCompleted?: () => void;
}

const DEFAULT_VALUES: RefillDenyDraft = {
  denialReason: DEFAULT_REFILL_DENIAL_REASON,
  note: '',
};

/**
 * Deny a pending refill with a required denial reason and optional note.
 * Mutation success invalidates ``rxKeys.pendingRefillsDetailed()`` (prefix match).
 */
export function RefillDenyForm({ refillId, onCompleted }: RefillDenyFormProps) {
  const mutation = useDenyRefillMutation();
  const { form, handleSubmit } = useDetailAction({
    schema: refillDenyDraftSchema,
    defaultValues: DEFAULT_VALUES,
    mutation,
    buildVariables: (draft: RefillDenyDraft) => ({
      refillId,
      data: {
        DenialReason: draft.denialReason,
        Note: nullIfBlank(draft.note),
      } satisfies DenyRefillRequestT,
    }),
    successMessage: 'Refill denied.',
    errorTitle: 'Could not deny refill.',
    logKey: 'deny refill',
    logContext: { refillId },
    onCompleted,
  });

  return (
    <DetailActionForm
      mutation={mutation}
      onSubmit={handleSubmit}
      submitLabel="Deny refill"
      pendingLabel="Denying…"
      submitVariant="destructive"
    >
      <FormField
        form={form}
        type="select"
        name="denialReason"
        label="Denial reason"
        options={[...REFILL_DENIAL_REASON_OPTIONS]}
        placeholder="Select a denial reason"
      />
      <FormField
        form={form}
        type="textarea"
        name="note"
        label="Note"
        placeholder="Optional note for the pharmacy"
      />
    </DetailActionForm>
  );
}
